import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import api from "../../api";

const COMMENT_MAX = 200;

/**
 * The post-class prompt: stars, a couple of taps, and out.
 *
 * Deliberately small — a student who has just left a lesson will not fill in a survey, so only
 * the rating is required and Skip is always available.
 */
const ClassFeedbackDialog = ({ open, cls, presets, maxPresets = 2, onClose, onSaved }) => {
  const [rating, setRating] = useState(0);
  const [selected, setSelected] = useState([]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Seed from an existing submission each time the dialog opens, so editing shows what they said.
  useEffect(() => {
    if (!open) return;
    setRating(cls?.my_feedback?.rating ?? 0);
    setSelected(cls?.my_feedback?.preset_keys ?? []);
    setComment(cls?.my_feedback?.comment ?? "");
    setError(null);
  }, [open, cls]);

  const togglePreset = (key) => {
    setSelected((current) => {
      if (current.includes(key)) return current.filter((k) => k !== key);
      if (current.length >= maxPresets) return current;
      return [...current, key];
    });
  };

  const submit = async () => {
    if (!rating) {
      setError("Please choose a star rating.");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const res = await api.post(`student/classes/${cls.id}/feedback`, {
        rating,
        preset_keys: selected,
        comment: comment.trim() ? comment.trim() : null,
      });
      onSaved?.(cls.id, res.data?.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || "Could not save your feedback. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5, fontWeight: 700 }}>How was today's class?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {cls?.name}
          {cls?.tutor?.full_name ? ` · ${cls.tutor.full_name}` : ""}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", my: 1.5 }}>
          <Rating
            value={rating}
            onChange={(_, value) => setRating(value ?? 0)}
            size="large"
            aria-label="Class rating"
          />
        </Box>

        {presets.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Anything stand out? (optional, pick up to {maxPresets})
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1, mb: 2 }}>
              {presets.map((preset) => {
                const active = selected.includes(preset.key);
                return (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    size="small"
                    onClick={() => togglePreset(preset.key)}
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    // Greying out unpicked chips at the cap explains the limit without an error.
                    disabled={!active && selected.length >= maxPresets}
                  />
                );
              })}
            </Stack>
          </>
        )}

        <TextField
          label="Anything else? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
          fullWidth
          size="small"
          multiline
          minRows={2}
          inputProps={{ maxLength: COMMENT_MAX }}
          helperText={`${comment.length}/${COMMENT_MAX}`}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Skip
        </Button>
        <Button onClick={submit} disabled={saving || !rating} variant="contained">
          {saving ? "Saving…" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClassFeedbackDialog;
